import React from "react"
import { SUBJECT_META, activityScoreColor, shortDate } from "../learnerReportModel"
import type { PathItem } from "../learnerReportModel"
import { BasketBadge, ChevronRight, EmptyNote, SubjectChip, WhenBadge } from "../reportUi"
import type { ReportView } from "./reportView"

const Arrow = () => <ChevronRight size={20} />

const StepCard = ({
  item,
  onPlan,
}: {
  item: PathItem
  onPlan: () => void
}) => {
  const style: React.CSSProperties =
    item.when === "NOW"
      ? { flex: "1.3 1 220px", border: "2px solid #1F2F6B" }
      : item.when === "NEXT"
      ? { flex: "1.3 1 220px", border: "1px solid #D5DAE4" }
      : { flex: "1 1 180px", border: "1px dashed #CBD1DD" }
  return (
    <div className="sr-road-card" style={{ ...style, background: "#FFFFFF" }}>
      <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <WhenBadge when={item.when} />
        {item.when !== "THEN" ? <BasketBadge basket={item.basket} /> : null}
      </span>
      <span style={{ fontSize: 15, fontWeight: 700 }}>{item.title}</span>
      <span className="sr-muted-12">{item.meta}</span>
      {item.when === "NOW" ? (
        <button type="button" className="sr-peach-btn sr-no-print" onClick={onPlan}>
          + Lesson Plan
        </button>
      ) : null}
    </div>
  )
}

const RoadmapTab: React.FC<{ view: ReportView }> = ({ view }) => {
  return (
    <>
      {view.subjects.map(subject => {
        const sk = view.skills[subject]
        const recs = view.recs[subject]
        const path = recs.progress.slice(0, 3)
        const done = view.groups.find(g => g.subject === subject)
        const year = sk.currentYear
        const pct = sk.total ? Math.max(2, (sk.passed / sk.total) * 100) : 2
        const bestBySource = new Map<string, number>()
        view.groups.forEach(g => {
          if (g.subject === subject && g.best !== null) bestBySource.set(g.sourceId, g.best)
        })

        return (
          <section key={subject} aria-label={`${SUBJECT_META[subject].label} roadmap`} className="sr-card sr-section" style={{ gap: 18 }}>
            <div className="sr-head-center">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <SubjectChip subject={subject} />
                <h2 className="sr-h2">
                  {year !== null ? `Year ${year} path to Year ${year + 1}` : "Learning path"}
                </h2>
              </div>
              <span className="sr-muted">{sk.passed} of {sk.total} skills passed</span>
            </div>

            {path.length === 0 && !done ? (
              <EmptyNote>No activity or recommendations yet for {SUBJECT_META[subject].label}.</EmptyNote>
            ) : (
              <div className="sr-road-row">
                {done ? (
                  <>
                    <div className="sr-road-card" style={{ flex: "1 1 180px", background: "#F6F7FB", border: "1px solid #E3E6EE", gap: 6 }}>
                      <span className="sr-road-label" style={{ color: "#1E6E40" }}>DONE</span>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{done.title}</span>
                      <span className="sr-muted-12">
                        {done.best === null
                          ? "Not scored"
                          : done.tries.length > 1
                          ? `Best ${done.best.toFixed(1)} after ${done.tries.length} tries`
                          : `${done.best.toFixed(1)}${done.best >= 90 ? " · skill passed" : ""}`}{" "}
                        · {shortDate(done.last)}
                      </span>
                    </div>
                    {path.length || year !== null ? <Arrow /> : null}
                  </>
                ) : null}
                {path.map((p, i) => (
                  <React.Fragment key={p.sourceId}>
                    <StepCard item={p} onPlan={view.planLesson} />
                    {i < path.length - 1 || year !== null ? <Arrow /> : null}
                  </React.Fragment>
                ))}
                {year !== null ? (
                  <div className="sr-road-card" style={{ flex: "1 1 180px", background: "#FFF6E0", border: "1px solid #F0DBA6" }}>
                    <span className="sr-road-label" style={{ color: "#7A5410" }}>GOAL</span>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>
                      Unlock Year {year + 1} {SUBJECT_META[subject].label}
                    </span>
                    <span style={{ fontSize: 12, color: "#6B5320" }}>
                      Pass all {sk.total} Year {year} skills (score 9+)
                    </span>
                    <div role="img" aria-label={`${sk.passed} of ${sk.total} passed`} className="sr-goal-bar">
                      <div style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {recs.relearn.length ? (
              <div className="sr-relearn">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <BasketBadge basket="Relearn" />
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Revisit alongside the path</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {recs.relearn.map(r => {
                    const best = bestBySource.get(r.sourceId) ?? null
                    return (
                      <div key={r.sourceId} className="sr-relearn-item" title={r.meta}>
                        <span className="sr-dot" style={{ background: "#D7354B" }} />
                        <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{r.title}</span>
                        {best !== null ? (
                          <span className="sr-mono" style={{ fontSize: 13, fontWeight: 700, color: activityScoreColor(best) }}>
                            {best.toFixed(1)}
                          </span>
                        ) : null}
                        <button type="button" className="sr-peach-btn sr-no-print" style={{ alignSelf: "auto", padding: "0 12px" }} onClick={view.planLesson}>
                          + Homework
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </section>
        )
      })}
      <p className="sr-footnote">
        The path updates after each completed activity. A skill passes at a score of 9 or above.
      </p>
    </>
  )
}

export default RoadmapTab
